import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Trophy, Users, MapPin, Award } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export default function TeamDetailPage() {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const teamData = location.state || {};
  const decodedTeamName = decodeURIComponent(teamName);

  const mockTeamData = {
    "Thunder Bolts": {
      logo: "⚡",
      founded: "2018",
      coach: "Mike Johnson",
      captain: "Alex Rodriguez",
      homeVenue: "Sports Complex A",
      wins: 12,
      losses: 4,
      winPercentage: 75.0,
      ranking: 3,
      totalPoints: 1240,
      players: [
        {
          id: 1,
          name: "Alex Rodriguez",
          position: "Point Guard",
          number: 23,
          height: "6'2\"",
          experience: "5 years",
          points: 18.5,
          assists: 7.2,
          rebounds: 4.8,
          isCaptain: true,
          avatar: "🏀"
        },
        {
          id: 2,
          name: "Jordan Smith",
          position: "Shooting Guard",
          number: 15,
          height: "6'4\"",
          experience: "3 years",
          points: 22.1,
          assists: 3.8,
          rebounds: 5.2,
          isCaptain: false,
          avatar: "🔥"
        },
        {
          id: 3,
          name: "Marcus Williams",
          position: "Power Forward",
          number: 11,
          height: "6'8\"",
          experience: "6 years",
          points: 15.7,
          assists: 2.1,
          rebounds: 9.4,
          isCaptain: false,
          avatar: "💪"
        },
        {
          id: 4,
          name: "David Brown",
          position: "Center",
          number: 32,
          height: "6'10\"",
          experience: "4 years",
          points: 12.3,
          assists: 1.5,
          rebounds: 11.2,
          isCaptain: false,
          avatar: "🏆"
        },
        {
          id: 5,
          name: "Chris Davis",
          position: "Small Forward",
          number: 8,
          height: "6'6\"",
          experience: "2 years",
          points: 14.8,
          assists: 4.3,
          rebounds: 6.1,
          isCaptain: false,
          avatar: "⭐"
        }
      ]
    },
    "Lightning Hawks": {
      logo: "🦅",
      founded: "2019",
      coach: "Sarah Thompson",
      captain: "Emily Chen",
      homeVenue: "Metro Arena",
      wins: 14,
      losses: 2,
      winPercentage: 87.5,
      ranking: 1,
      totalPoints: 1580,
      players: [
        {
          id: 6,
          name: "Emily Chen",
          position: "Point Guard",
          number: 10,
          height: "5'8\"",
          experience: "7 years",
          points: 21.3,
          assists: 9.1,
          rebounds: 4.2,
          isCaptain: true,
          avatar: "🦅"
        },
        {
          id: 7,
          name: "Jessica Park",
          position: "Shooting Guard",
          number: 24,
          height: "5'10\"",
          experience: "4 years",
          points: 19.8,
          assists: 4.5,
          rebounds: 5.7,
          isCaptain: false,
          avatar: "🎯"
        },
        {
          id: 8,
          name: "Amanda Wilson",
          position: "Power Forward",
          number: 20,
          height: "6'1\"",
          experience: "5 years",
          points: 16.2,
          assists: 3.1,
          rebounds: 8.9,
          isCaptain: false,
          avatar: "💎"
        },
        {
          id: 9,
          name: "Lisa Martinez",
          position: "Center",
          number: 33,
          height: "6'3\"",
          experience: "6 years",
          points: 13.9,
          assists: 1.8,
          rebounds: 10.5,
          isCaptain: false,
          avatar: "🔥"
        },
        {
          id: 10,
          name: "Kelly Robinson",
          position: "Small Forward",
          number: 7,
          height: "5'11\"",
          experience: "3 years",
          points: 17.4,
          assists: 5.2,
          rebounds: 6.8,
          isCaptain: false,
          avatar: "⚡"
        }
      ]
    }
  };

  const team = mockTeamData[decodedTeamName] || {
    logo: teamData.teamLogo || "🏀",
    founded: "2020",
    coach: "Unknown",
    captain: "Unknown",
    homeVenue: "Unknown",
    wins: 0,
    losses: 0,
    winPercentage: 0,
    ranking: "N/A",
    totalPoints: 0,
    players: []
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePlayerClick = (player) => {
    // Could navigate to individual player profile page
  };

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-6xl mx-auto">
        {/* Sport Type Badge - Right Aligned */}
        {teamData.sportType && (
          <div className="flex justify-end mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${theme.backgroundSecondary} ${theme.text}`}>
              <Award className="w-4 h-4 mr-1" />
              {teamData.sportType}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
          >
            <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
          </button>
          <div className="flex items-center gap-x-4">
            <span className="text-6xl">{team.logo}</span>
            <div>
              <h1 className={`text-3xl font-bold ${theme.text}`}>{decodedTeamName}</h1>
              <p className={`${theme.textSecondary} flex items-center mt-1`}>
                <MapPin className="w-4 h-4 mr-1" />
                {team.homeVenue}
                {teamData.tournament && (
                  <>
                    <span className="mx-2">•</span>
                    <Trophy className="w-4 h-4 mr-1" />
                    {teamData.tournament}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Team Stats */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
            <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
            Team Statistics
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{team.ranking}</div>
              <div className={`text-sm ${theme.textMuted}`}>Ranking</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold text-green-600`}>{team.wins}</div>
              <div className={`text-sm ${theme.textMuted}`}>Wins</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold text-red-600`}>{team.losses}</div>
              <div className={`text-sm ${theme.textMuted}`}>Losses</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{team.winPercentage}%</div>
              <div className={`text-sm ${theme.textMuted}`}>Win Rate</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{team.totalPoints.toLocaleString()}</div>
              <div className={`text-sm ${theme.textMuted}`}>Total Points</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{team.players.length}</div>
              <div className={`text-sm ${theme.textMuted}`}>Players</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
              <h4 className={`font-semibold ${theme.text} mb-1`}>Head Coach</h4>
              <p className={`${theme.textSecondary}`}>{team.coach}</p>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
              <h4 className={`font-semibold ${theme.text} mb-1`}>Team Captain</h4>
              <p className={`${theme.textSecondary}`}>{team.captain}</p>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4`}>
              <h4 className={`font-semibold ${theme.text} mb-1`}>Founded</h4>
              <p className={`${theme.textSecondary}`}>{team.founded}</p>
            </div>
          </div>
        </div>

        {/* Players List */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
            <Users className="w-6 h-6 mr-2 text-blue-500" />
            Player Roster
          </h2>

          {team.players.length > 0 ? (
            <div className="space-y-4">
              {team.players.map((player) => (
                <div
                  key={player.id}
                  className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
                  onClick={() => handlePlayerClick(player)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-4">
                      <div className="text-3xl">{player.avatar}</div>
                      <div>
                        <div className="flex items-center gap-x-2">
                          <h3 className={`text-lg font-semibold ${theme.text}`}>
                            #{player.number} {player.name}
                          </h3>
                          {player.isCaptain && (
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              Captain
                            </span>
                          )}
                        </div>
                        <p className={`${theme.textSecondary}`}>
                          {player.position} • {player.height} • {player.experience}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-6 text-center">
                      <div>
                        <div className={`text-lg font-bold ${theme.text}`}>{player.points}</div>
                        <div className={`text-xs ${theme.textMuted}`}>PPG</div>
                      </div>
                      <div>
                        <div className={`text-lg font-bold ${theme.text}`}>{player.assists}</div>
                        <div className={`text-xs ${theme.textMuted}`}>APG</div>
                      </div>
                      <div>
                        <div className={`text-lg font-bold ${theme.text}`}>{player.rebounds}</div>
                        <div className={`text-xs ${theme.textMuted}`}>RPG</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${theme.text} mb-2`}>No players found</h3>
              <p className={`${theme.textSecondary}`}>Player roster information is not available for this team.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}